import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environments';

declare let gtag: Function;

const ROUTE_TITLES: Record<string, string> = {
  'landing-page':                   'Rap Almanac | Home',
  'signin':                         'Rap Almanac | Sign In',
  'create-account':                 'Rap Almanac | Create Account',
  'password-help':                  'Rap Almanac | Password Help',
  'privacy-notice':                 'Rap Almanac | Privacy Policy',
  'learning':                       'Rap Almanac | Learning',
  'dashboard/explore':              'Rap Almanac | Explore',
  'dashboard/cards':                'Rap Almanac | Cards',
  'dashboard/analytics':            'Rap Almanac | Analytics',
  'dashboard/trending':             'Rap Almanac | Trending Words',
  'dashboard/rosters':              'Rap Almanac | Rosters',
  'dashboard/functions':            'Rap Almanac | Functions',
  'dashboard/roles':                'Rap Almanac | Roles',
  'dashboard/users':                'Rap Almanac | Users',
  'dashboard/database/songs':       'Rap Almanac | Songs Database',
  'dashboard/database/producers':   'Rap Almanac | Producers Database',
  'dashboard/database/artists':     'Rap Almanac | Artists Database',
  'dashboard/database/albums':      'Rap Almanac | Albums Database',
  'dashboard/contributions/songs':  'Rap Almanac | Song Contributions',
  'dashboard/contributions/producers': 'Rap Almanac | Producer Contributions',
  'dashboard/contributions/artists': 'Rap Almanac | Artist Contributions',
  'dashboard/contributions/albums': 'Rap Almanac | Album Contributions',
  'dashboard/api-docs':             'Rap Almanac | API Docs',
  'profile':                        'Rap Almanac | Profile',
};

function titleFromUrl(url: string): string {
  // Strip query params and leading slash
  const path = url.replace(/^\//, '').split('?')[0];
  // Exact match first
  if (ROUTE_TITLES[path]) return ROUTE_TITLES[path];
  // Prefix match (handles /:id sub-routes)
  const prefix = Object.keys(ROUTE_TITLES)
    .filter((k) => path.startsWith(k + '/'))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? ROUTE_TITLES[prefix] : 'Rap Almanac';
}

@Injectable({
  providedIn: 'root',
})
export class Ga4Service {
  private initialized = false;

  constructor(private router: Router, private titleService: Title) {}

  /**
   * Route-title tracking. Functional only — sets no cookies and loads no
   * scripts — so it runs for every visitor regardless of cookie consent.
   */
  startTitleTracking(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.titleService.setTitle(titleFromUrl(event.urlAfterRedirects));
      });
  }

  /**
   * Loads Google Analytics. Call ONLY after the visitor accepts cookies — this
   * is what injects gtag.js and begins setting the _ga cookies. No-op until then.
   */
  init(): void {
    const measurementId = environment.ga4MeasurementId;
    if (!measurementId || this.initialized) {
      return;
    }
    this.initialized = true;

    // Load gtag.js script asynchronously
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = function () {
      (window as any).dataLayer.push(arguments);
    };
    gtag('js', new Date());
    gtag('config', measurementId, { send_page_view: false });

    // Consent may be granted mid-session, after the current route's
    // NavigationEnd already fired — send a page view for where we are now.
    const currentUrl = this.router.url;
    gtag('config', measurementId, {
      page_path: currentUrl,
      page_location: window.location.href,
      page_title: titleFromUrl(currentUrl),
    });

    // Track page views on subsequent route changes.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        gtag('config', measurementId, {
          page_path: event.urlAfterRedirects,
          page_location: window.location.href,
          page_title: titleFromUrl(event.urlAfterRedirects),
        });
      });
  }

  event(eventName: string, params?: Record<string, any>): void {
    if (!this.initialized) {
      return;
    }
    gtag('event', eventName, params);
  }
}
